import { makeAutoObservable, runInAction, reaction } from "mobx"
import {taxAuthApi} from "../api/taxAuth.js";
import {webOrderEstimateApi} from "../api/webOrderEstimate.js";
import {orderPaymentApi} from "../api/orderPayment.js";

export class TaxCertOrderStore {
    clientReference = null;
    taxAuthorityId = null;
    taxAuthority = null;
    taxAuthorityName = null;
    productName = null;
    productFee = 0.0;
    serviceFee = 0.0;
    productFeeTax = 0.0;
    serviceFeeTax = 0.0;
    totalTaxes = 0.0;
    total = 0.0;
    webOrderEstimateApi = null;
    orderPaymentApi = null;
    taxAuthApi = null;
    loadingEstimate = false;
    searchType = null;
    searchTerm = null;
    validOrder = false;
    searchTermValid = true;
    searchTermErrorMsg = null;
    clientSecret = null;
    paymentProvider = "Stripe";
    paymentReference = null;
    taxAuthorities = [];
    selectedTaxAuthorityOffline = false;
    selectedTaxAuthorityOfflineReason = null;
    constructor(webOrderEstimateApi, taxAuthApi, orderPaymentApi, taxAuthOrderApi) {
        makeAutoObservable(this);
        this.webOrderEstimateApi = webOrderEstimateApi;
        this.taxAuthApi = taxAuthApi;
        this.orderPaymentApi = orderPaymentApi;
        this.taxAuthOrderApi = taxAuthOrderApi;
    }

    async startNewOrder() {
        this.clearOrder();
        const [taxAuths, taxAuthStatuses] = await Promise.all([
            taxAuthApi.getTaxAuthorities(),
            taxAuthApi.getTaxAuthorityStatus()
        ]);
        const taxAuthStatusMap = new Map((taxAuthStatuses || []).map((status) => [status.taxAuthorityId, status]));
        const mergedTaxAuths = (taxAuths || []).map((taxAuth) => {
            const status = taxAuthStatusMap.get(taxAuth.id);
            return {
                ...taxAuth,
                online: status?.online ?? taxAuth.online,
                offlineReason: status?.offlineReason ?? taxAuth.offlineReason ?? null
            };
        });
        runInAction(() => {
            this.taxAuthorities = mergedTaxAuths;
        })
        
    }
    clearOrder() {
        runInAction(() => {
            this.taxAuthorityName = null;
            this.taxAuthorityId = null;
            this.productFee = 0.0;
            this.productName = 0.0;
            this.serviceFee = 0.0;
            this.productFeeTax = 0.0;
            this.serviceFeeTax = 0.0;
            this.totalTaxes = 0.0;
            this.total = 0.0;
            this.searchType = null;
            this.searchTerm = null;
            this.clientReference = null;
            this.searchTermValid = true;
            this.searchTermErrorMsg = null;
            this.validOrder = false;
            this.clientSecret = null;
            this.paymentProvider = "Stripe";
            this.paymentReference = null;
            this.loadingEstimate = false;
            this.selectedTaxAuthorityOffline = false;
            this.selectedTaxAuthorityOfflineReason = null;
        })
    }

    async getClientSecret(){
        var result = await orderPaymentApi.getClientSecret(this.taxAuthorityId, "TaxCertificate");

        runInAction(() =>{
            this.clientSecret = result.clientSecret;
            this.paymentProvider = result.paymentProvider || "Stripe";
            this.paymentReference = result.paymentReference || null;
        })
    }

    async invalidatePaymentIntent(paymentReference){
        try {
            await orderPaymentApi.invalidatePaymentIntent(paymentReference, this.paymentProvider);
        } catch (e) {
            console.error("Failed to invalidate payment intent", e);
        }
        runInAction(() => {
            this.clientSecret = null;
            this.paymentReference = null;
        });
    }

    setClientReference(clientReference){
        this.clientReference = clientReference;
        this.validateOrder();
    }

    setSearchTerm(searchTerm){
        this.searchTerm = searchTerm;
        this.validateOrder();
    }

    setSearchType(searchType) {
        this.searchType = searchType;
        this.validateOrder();
    }

    setSelectedTaxAuthorityId(taxAuthorityId){
        console.log("new tax auth id: ", taxAuthorityId);
        this.taxAuthorityId = taxAuthorityId === '' ? null : taxAuthorityId;
        this.taxAuthority = null;
        this.selectedTaxAuthorityOffline = false;
        this.selectedTaxAuthorityOfflineReason = null;
        if (this.taxAuthorityId !== null){
            this.taxAuthority = this.taxAuthorities.find(taxAuth => taxAuth.id === this.taxAuthorityId);
            this.selectedTaxAuthorityOffline = this.taxAuthority?.online === false;
            this.selectedTaxAuthorityOfflineReason = this.taxAuthority?.offlineReason || "This municipality is currently offline.";
        }
        this.updateWebOrderEstimate()
        this.validateOrder();
    }

    validPid () {
        // Define the regular expression pattern
        const pattern = /^\d{3}-\d{3}-\d{3}$/;
        return pattern.test(this.searchTerm);

    }
    validateOrder() {
        if (this.taxAuthorityId === null){
            this.validOrder = false;
            return;
        }
        if (this.selectedTaxAuthorityOffline) {
            this.validOrder = false;
            return;
        }
        if (this.searchType === null){
            this.validOrder = false;
            return;
        }
        if (this.searchTerm === null){
            this.searchTermValid = false;
            this.searchTermErrorMsg = "You must enter a search term.";
            this.validOrder = false;
            return;
        }
        console.log('searchType: ', this.searchType);
        if (this.searchType === 'pid'){
            if (!this.validPid()){
                this.searchTermValid = false;
                this.searchTermErrorMsg = 'Invalid PID format.'
                this.validOrder = false;
                return;
            }
        }
        this.searchTermValid = true;
        this.searchTermErrorMsg = null;
        this.validOrder = true;
    }

    async updateWebOrderEstimate() {
        if (this.taxAuthorityId === null || this.taxAuthorityId === ''){
            runInAction(() => {
                this.taxAuthorityName = null;
                this.productFee = 0.0;
                this.productName = 0.0;
                this.serviceFee = 0.0;
                this.productFeeTax = 0.0;
                this.serviceFeeTax = 0.0;
                this.totalTaxes = 0.0;
                this.total = 0.0;
                this.loadingEstimate = false;
            });
        } else {
            try {
                runInAction(() =>{
                    this.loadingEstimate = true;
                })
                const estimate = await webOrderEstimateApi.getWebOrderEstimate(this.taxAuthorityId, "TaxCertificate");
                const taxAuthInfo = await taxAuthApi.getTaxAuthInfo(this.taxAuthorityId);
                runInAction(() => {
                    this.taxAuthorityName = taxAuthInfo.name;
                    this.productFee = estimate.productFee;
                    this.productName = estimate.productName;
                    this.serviceFee = estimate.serviceFee;
                    this.productFeeTax = estimate.productFeeTax;
                    this.serviceFeeTax = estimate.serviceFeeTax;
                    this.totalTaxes = this.productFeeTax + this.serviceFeeTax;
                    this.total = estimate.total;
                    this.selectedTaxAuthorityOffline = estimate.available === false;
                    this.selectedTaxAuthorityOfflineReason = estimate.unavailableReason || null;
                });
            } catch (e) {
                runInAction(() => {
                    this.productFee = 0.0;
                    this.productName = 0.0;
                    this.serviceFee = 0.0;
                    this.productFeeTax = 0.0;
                    this.serviceFeeTax = 0.0;
                    this.totalTaxes = 0.0;
                    this.total = 0.0;
                });
                const message = e?.response?.data?.errors?.[0]?.message || e?.response?.data?.message;
                if (message) {
                    runInAction(() => {
                        this.selectedTaxAuthorityOffline = true;
                        this.selectedTaxAuthorityOfflineReason = message;
                    });
                }
            } finally {
                runInAction(() => {
                    this.loadingEstimate = false;
                    this.validateOrder();
                })
            }
        }


    }

    async finalizeOrder(paymentIntent) {
        const paymentReference = this.paymentProvider === "Stripe"
            ? paymentIntent?.id
            : this.paymentReference;
        const result = await this.taxAuthOrderApi.finishOrder(
            this.clientReference,
            paymentReference,
            this.paymentProvider,
            this.taxAuthorityId,
            "TaxCertificate",
            this.searchType,
            this.searchTerm
        );
        return result;
    }
}
